import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, MapPin, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import { useLocation } from '@/contexts/LocationContext';
import api from '@/lib/api';

const ICON_MAP = { beer: '🍺', music: '🎵', moon: '🌙', sun: '☀️', star: '⭐', heart: '❤️', utensils: '🍽️', palette: '🎨', glass: '🍸', book: '📚', building: '🏢' };

export default function AutoGuideListPage() {
  const { state, city, neighborhood } = useParams();
  const { currentCity } = useLocation();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (state) params.set('state', state.toUpperCase());
    if (city) params.set('city', city.charAt(0).toUpperCase() + city.slice(1));
    if (neighborhood) params.set('neighborhood', neighborhood);
    if (!state && currentCity) params.set('city', currentCity.city);
    
    api.get(`/guides/auto?${params.toString()}&limit=50`)
      .then(res => setGuides(res.data.guides || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [state, city, neighborhood, currentCity]);

  const title = neighborhood ? `${neighborhood.replace(/-/g, ' ')} Guides`.replace(/\b\w/g, l => l.toUpperCase())
    : city ? `${city.charAt(0).toUpperCase() + city.slice(1)} Guides`
    : state ? `${state.toUpperCase()} Guides`
    : `${currentCity?.city || 'Local'} Guides`;

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Link to="/guides" className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> City Guides</Link>
        
        <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-slate-400 mb-8">{guides.length} curated guide{guides.length !== 1 ? 's' : ''}</p>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : guides.length === 0 ? (
          <div className="text-center py-20"><BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No guides available yet for this area. Guides are auto-generated as partners join.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map(guide => (
              <Link key={guide.id} to={`/guides/auto/${guide.location}/${guide.slug}`}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all h-full group">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{ICON_MAP[guide.icon] || '📍'}</span>
                      <Badge className="bg-indigo-600/20 text-indigo-300 border-indigo-500/30 text-xs">{guide.category}</Badge>
                    </div>
                    <h3 className="font-bold text-white text-lg group-hover:text-indigo-400 transition-colors">{guide.title}</h3>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2">{guide.description}</p>
                    <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      <span>{guide.item_count} listing{guide.item_count !== 1 ? 's' : ''}</span>
                      {guide.neighborhood && <span>• {guide.neighborhood}</span>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
